// A pointer press (mouse, touch or pen) moves focus when it starts, before its click completes. That
// focus move is the start of an outside click, which the caller's click handling already owns
// (pairing mousedown with mouseup, so a press dragged back inside doesn't count). Tracked at the
// document so it's known before any focusout fires; pointerdown as well as mousedown, since a touch
// whose touchstart was preventDefault()ed gets no compatibility mousedown. Some presses never get
// their mouseup/pointerup (released outside the window or over a cross-origin iframe, a native
// drag, a press that opened a native alert), so a key press, a drag's end, the window losing focus
// or a mouse move with no button held ends it too. Only the press-start events are listened for
// all the time; the release listeners are added for a press and removed when it ends. The press's
// own mouseup/pointerup ends it a task later: its click comes after them, and focus a click
// handler moves is still part of that press. A new press cancels that pending end, so a quick
// second press isn't cut short by the first one's.
let pointerPressed = false;
if (typeof window !== "undefined") {
  let pendingEnd: ReturnType<typeof setTimeout> | undefined;
  const endSoon = () => {
    clearTimeout(pendingEnd);
    pendingEnd = setTimeout(release);
  };
  // Capture-phase listeners on window also see every element's blur (blur doesn't bubble, but it
  // does capture through window), and the press's own focus move blurs an element: only the
  // window itself losing focus ends the press.
  const onWindowBlur = (event: Event) => {
    if (event.target === window) {
      release();
    }
  };
  const onMouseMove = (event: MouseEvent) => {
    if (!event.buttons) {
      release();
    }
  };
  const releaseOn: [EventTarget, string, EventListener][] = [
    [document, "mouseup", endSoon],
    [document, "pointerup", endSoon],
    [document, "pointercancel", release],
    [document, "keydown", release],
    [document, "dragend", release],
    [document, "mousemove", onMouseMove as EventListener],
    [window, "blur", onWindowBlur],
  ];
  // Hoisted on purpose: `releaseOn` and the handlers above reference it before this line.
  // eslint-disable-next-line no-inner-declarations
  function release(): void {
    clearTimeout(pendingEnd);
    pointerPressed = false;
    releaseOn.forEach(([target, type, listener]) =>
      target.removeEventListener(type, listener, true),
    );
  }
  const press = () => {
    clearTimeout(pendingEnd);
    if (!pointerPressed) {
      pointerPressed = true;
      releaseOn.forEach(([target, type, listener]) =>
        target.addEventListener(type, listener, true),
      );
    }
  };
  document.addEventListener("pointerdown", press, true);
  document.addEventListener("mousedown", press, true);
}

/**
 * Whether a focusout moves keyboard focus to a known element outside `containers` (e.g. Tab past
 * a popup's last control, or Shift+Tab before its trigger), the keyboard counterpart of an outside
 * click. False when:
 * - a pointer press moved focus: that's an outside click, left to the caller's click handling;
 * - there's no relatedTarget: a click on non-focusable chrome inside the popup, or the window
 *   itself losing focus, neither of which leaves the popup;
 * - focus moved into a CDK overlay the popup isn't in: a dialog or detached panel opened from the
 *   popup, which hands focus back when it closes. A popup that is itself inside a dialog still
 *   closes when focus moves elsewhere in that same dialog.
 */
export function focusMovedOutside(
  event: FocusEvent,
  ...containers: (Element | null | undefined)[]
): boolean {
  const next = event.relatedTarget;
  if (pointerPressed || !(next instanceof Element)) {
    return false;
  }
  const hosts = containers.filter((container): container is Element => !!container);
  if (hosts.some((host) => host.contains(next))) {
    return false;
  }
  const nextOverlay = next.closest(".cdk-overlay-pane") ?? next.closest(".cdk-overlay-container");
  return !nextOverlay || hosts.some((host) => nextOverlay.contains(host));
}
